import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Icon } from "@/components/ds";
import { Loader2 } from "@/components/ds/icons";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  responsiveDialogBodyClass,
} from "@/components/ui/ResponsiveDialog";
import { cn } from "@/lib/utils";
import type { DocumentoTipo } from "@/types/documento-tipo";

/**
 * "Documentos que exige tu empresa" — el modal donde se elige qué tipos del
 * catálogo cuentan como exigibles.
 *
 * Es la pieza que le da sentido a todo el resto: sin esta declaración, el
 * catálogo de 48 tipos es una lista de referencia y ninguna pantalla puede
 * medir cumplimiento contra él sin convertir un checklist opcional en 48
 * incumplimientos aparentes.
 *
 * La selección se edita en estado LOCAL y recién se manda al apretar "Listo".
 * Marcar 24 casillas con un PUT por clic serían 24 requests y 24 recálculos
 * del denominador en las pantallas de atrás.
 *
 * ALCANCE — es una decisión de la EMPRESA, no del trabajador que se esté
 * mirando. La marca vive en `persona_documento_tipos_exigidos`, con clave
 * `(empresa_id, tipo_documento_id)`: no existe una versión por persona. Por eso
 * el aviso de alcance es fijo y no un detalle de copy — sin él la pantalla
 * miente sobre lo que hace el botón.
 *
 * El aviso depende del `origen` porque hay DOS puntos de entrada y el riesgo no
 * es el mismo. Desde la ficha de alguien está el malentendido caro: desmarcar
 * "Licencia de conducir" porque ESTE trabajador no maneja le cambia el
 * denominador a toda la dotación, y ahí el aviso tiene que contrastar
 * explícitamente contra la persona que se está mirando. Desde el listado no hay
 * ninguna persona a la vista: ese mismo contraste ("no sólo al que estás
 * viendo") referencia algo que no existe en pantalla y el aviso se lee roto.
 *
 * DESVIACIÓN del prototipo: no está la columna de vigencia por defecto
 * ("vence cada 1 año"). Es un tercer concepto —una vigencia por TIPO— que hoy
 * no existe en la base y que además implica decidir si al subir un archivo la
 * fecha se autocompleta. Queda para su propio MR.
 */

export interface ConfigurarExigiblesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipos: DocumentoTipo[];
  /**
   * Dotación de la empresa. Sólo sirve para decir a cuántas personas alcanza
   * el cambio; si no se sabe, el aviso lo dice sin número.
   */
  dotacion?: number;
  /**
   * Desde dónde se abrió. Cambia el aviso de alcance: sólo desde la ficha de
   * una persona tiene sentido contrastar contra "el que estás viendo".
   */
  origen: "ficha" | "listado";
  onGuardar: (tipoIds: number[]) => Promise<void>;
}

export function ConfigurarExigiblesModal({
  open,
  onOpenChange,
  tipos,
  dotacion,
  origen,
  onGuardar,
}: ConfigurarExigiblesModalProps) {
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [q, setQ] = useState("");
  const [guardando, setGuardando] = useState(false);

  // Al abrir se parte SIEMPRE del estado guardado: si el usuario marcó cosas y
  // cerró con la X, esos cambios se descartan (no se guardó nada) y volver a
  // abrir no debe mostrarlos como si siguieran vivos.
  useEffect(() => {
    if (open) {
      setSeleccion(new Set(tipos.filter((t) => t.exigido).map((t) => t.id)));
      setQ("");
    }
  }, [open, tipos]);

  const filtrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return tipos;
    return tipos.filter(
      (t) => t.label.toLowerCase().includes(query) || t.grupo.toLowerCase().includes(query),
    );
  }, [tipos, q]);

  function toggle(id: number) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Marca o desmarca TODO lo que se está viendo, no el catálogo entero: con
   * un filtro puesto, "todos" que ignore el filtro es una trampa. */
  function marcarVisibles(marcar: boolean) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      for (const t of filtrados) {
        if (marcar) next.add(t.id);
        else next.delete(t.id);
      }
      return next;
    });
  }

  async function guardar() {
    setGuardando(true);
    try {
      await onGuardar([...seleccion]);
      onOpenChange(false);
    } catch {
      toast.error("No se pudo guardar. Intenta de nuevo.");
    } finally {
      setGuardando(false);
    }
  }

  const visiblesMarcados = filtrados.filter((t) => seleccion.has(t.id)).length;

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-[640px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Documentos que exige tu empresa</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Marca solo los que tu empresa realmente pide. Lo que dejes sin marcar sigue disponible
            para subir en cada ficha, pero no cuenta como faltante ni afecta el porcentaje.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className={cn(responsiveDialogBodyClass, "flex flex-col gap-3")}>
          {/* Alcance, fijo y arriba de todo: lo que se marque aplica a la
              dotación entera, se haya llegado desde donde se haya llegado. */}
          <div className="bg-primary-100 border-primary-200 flex items-start gap-2.5 rounded-lg border px-3 py-2.5">
            <Icon name="groups" size={17} className="text-primary-hover mt-px" />
            <span className="text-[12.5px] leading-[1.45]">
              Esto aplica a{" "}
              <strong className="font-semibold">
                {typeof dotacion === "number"
                  ? `los ${dotacion} trabajadores de tu empresa`
                  : "todos los trabajadores de tu empresa"}
              </strong>
              {origen === "ficha" ? ", no sólo al que estás viendo." : "."} Es la política de
              documentación de la empresa.
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[180px] flex-1">
              <Icon
                name="search"
                size={16}
                className="text-icon-soft absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar documento…"
                aria-label="Buscar documento"
                className="h-[34px] pl-8 text-xs"
              />
            </div>
            <Button
              variant="outline"
              size="xs"
              onClick={() => marcarVisibles(visiblesMarcados !== filtrados.length)}
              disabled={filtrados.length === 0}
            >
              {visiblesMarcados === filtrados.length ? "Desmarcar" : "Marcar"}
              {q.trim() ? " lo filtrado" : " todos"}
            </Button>
          </div>

          <div className="border-border-subtle max-h-[46vh] overflow-y-auto rounded-xl border">
            {filtrados.length === 0 ? (
              <p className="text-muted-foreground px-4 py-8 text-center text-[13.5px]">
                Ningún documento coincide con la búsqueda.
              </p>
            ) : (
              filtrados.map((tipo) => {
                const marcado = seleccion.has(tipo.id);
                return (
                  <label
                    key={tipo.id}
                    className="border-border-faint hover:bg-primary-50 flex cursor-pointer items-center gap-3 border-b px-4 py-2 transition-colors last:border-b-0"
                  >
                    <Checkbox
                      checked={marcado}
                      onCheckedChange={() => toggle(tipo.id)}
                      aria-label={`Exigir ${tipo.label}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-[12.5px]">{tipo.label}</span>
                    <span className="text-soft shrink-0 text-[11px]">{tipo.grupo}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <ResponsiveDialogFooter>
          <span className="text-muted-foreground mr-auto text-xs">
            <span className="fc-num">{seleccion.size}</span> de{" "}
            <span className="fc-num">{tipos.length}</span> tipos de documento exigidos por tu
            empresa
          </span>
          <Button onClick={guardar} disabled={guardando}>
            {guardando && <Loader2 className="h-4 w-4 animate-spin" />}
            Listo
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
